import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';

/**
 * 🎬 Interaktives How-To-Kurzvideo (animierte Schritt-für-Schritt-Simulation)
 * für den Barcode-Stapel / DHL-Schnelleinlagerung.
 *
 * Zeigt in 4 animierten Szenen:
 *  1) Pakete nur 1× in PaketPilot fotografieren (inkl. Doppelabgleich & Leitcode-Filter)
 *  2) DHL-Gerät „Schnelleinlagerung" + 🎙️ Mikrofon Piep-Erkennung (Grüner Blitz → nächster Barcode)
 *  3) ⏸️ Pause bei Kundenbetrieb (Position gespeichert) & 🖼️ Originalfoto zoombar
 *  4) 🕒 7-Tage-Archiv & Chronik-Filter nach Tag/Uhrzeit (Stapel jederzeit wiederherstellbar)
 */
const SCENE_DURATION_MS = 5500;

export default function BatchTutorialVideo({ compact = false }) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(true);
  const [scene, setScene] = useState(0);
  const [tick, setTick] = useState(0); // 0..100 Fortschritt innerhalb der Szene

  const scenes = [
    {
      badge: t('ui.batch.videoS1Badge'),
      title: t('ui.batch.videoS1Title'),
      desc: t('ui.batch.videoS1Desc'),
    },
    {
      badge: t('ui.batch.videoS2Badge'),
      title: t('ui.batch.videoS2Title'),
      desc: t('ui.batch.videoS2Desc'),
    },
    {
      badge: t('ui.batch.videoS3Badge'),
      title: `⏸️ ${t('ui.batch.videoS3Title')}`,
      desc: t('ui.batch.videoS3Desc'),
    },
    {
      badge: t('ui.batch.videoS4Badge'),
      title: `🕒 ${t('ui.batch.videoS4Title')}`,
      desc: t('ui.batch.videoS4Desc'),
    },
  ];

  useEffect(() => {
    if (!playing) return undefined;
    const stepMs = 100;
    const inc = (stepMs / SCENE_DURATION_MS) * 100;
    const id = setInterval(() => {
      setTick((prev) => {
        if (prev + inc >= 100) {
          setScene((s) => (s + 1) % scenes.length);
          return 0;
        }
        return prev + inc;
      });
    }, stepMs);
    return () => clearInterval(id);
  }, [playing, scenes.length]);

  const selectScene = (idx) => {
    setScene(idx);
    setTick(0);
  };

  // Sub-Animationsphasen innerhalb der Szene (0..100)
  const isBeepFlash = scene === 1 && ((tick >= 28 && tick <= 44) || (tick >= 68 && tick <= 84));
  const currentPkgNum = scene === 1 ? (tick < 36 ? 1 : tick < 76 ? 2 : 3) : 1;

  return (
    <div
      data-testid="batch-tutorial-video"
      style={{
        background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
        color: '#f8fafc',
        borderRadius: 14,
        border: '1px solid #334155',
        overflow: 'hidden',
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        marginTop: compact ? 8 : 12,
        marginBottom: compact ? 8 : 12,
      }}
    >
      {/* Top Header Bar (Video-Look) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'rgba(15, 23, 42, 0.75)',
          borderBottom: '1px solid #334155',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#38bdf8' }}>
          <span>🎬</span>
          <span>
            {t('ui.batch.videoHeader')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          style={{
            background: playing ? '#334155' : '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {playing
            ? `⏸ ${t('ui.batch.videoPause')}`
            : `▶ ${t('ui.batch.videoPlay')}`}
        </button>
      </div>

      {/* Animated Stage */}
      <div
        style={{
          position: 'relative',
          minHeight: compact ? 168 : 195,
          padding: compact ? '12px 14px' : '16px 18px',
          background: isBeepFlash
            ? 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)'
            : 'radial-gradient(circle at 50% 20%, #1e293b 0%, #0f172a 100%)',
          transition: 'background 0.18s ease',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Scene 0: Capture once + Double check + Leitcode filter */}
        {scene === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 10, flexWrap: 'wrap', margin: 'auto 0' }}>
            <div style={{ background: '#1e293b', border: '2px solid #38bdf8', borderRadius: 10, padding: '10px 12px', textAlign: 'center', minWidth: 125 }}>
              <div style={{ fontSize: 24 }}>📦 📸</div>
              <div style={{ fontSize: 12, fontWeight: 800, marginTop: 4 }}>
                {t('ui.batch.videoPhotoIn')}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                {t('ui.batch.videoDemoName')}
              </div>
            </div>
            <div style={{ fontSize: 20, color: '#38bdf8', fontWeight: 900 }}>➔</div>
            <div style={{ background: '#0f172a', border: '1px solid #22c55e', borderRadius: 10, padding: '8px 12px', fontSize: 11, lineHeight: 1.45 }}>
              <div style={{ color: '#4ade80', fontWeight: 800 }}>
                ✓ {t('ui.batch.videoCrossCheck')}
              </div>
              <div style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: 10 }}>00340434784304663887</div>
              <div style={{ color: '#f87171', fontSize: 10, textDecoration: 'line-through', opacity: 0.85 }}>
                {t('ui.batch.videoRoutingIgnored')}
              </div>
              <div style={{ color: '#38bdf8', fontWeight: 700, marginTop: 3 }}>
                📋 {t('ui.batch.videoAddedToBatch', { n: Math.min(3, Math.floor(tick / 30) + 1) })}
              </div>
            </div>
          </div>
        )}

        {/* Scene 1: DHL-Gerät Schnelleinlagerung + Mic Beep Detection + Green Flash */}
        {scene === 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 10, flexWrap: 'wrap', margin: 'auto 0' }}>
            {/* Simulated Phone Screen showing Barcode */}
            <div
              style={{
                background: isBeepFlash ? '#dcfce7' : '#ffffff',
                color: '#0f172a',
                borderRadius: 10,
                padding: '8px 12px',
                minWidth: 165,
                textAlign: 'center',
                boxShadow: isBeepFlash ? '0 0 20px rgba(74, 222, 128, 0.9)' : '0 4px 12px rgba(0,0,0,0.4)',
                border: isBeepFlash ? '3px solid #16a34a' : '2px solid #cbd5e1',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 800, color: '#334155', marginBottom: 4 }}>
                <span>📋 {t('ui.batch.videoParcelNum', { n: currentPkgNum })}</span>
                <span style={{ color: '#16a34a' }}>🎙️ {t('ui.batch.videoBeepOn')}</span>
              </div>
              {/* Crisp CSS Barcode */}
              <div
                style={{
                  height: 36,
                  background:
                    'repeating-linear-gradient(90deg, #000 0px, #000 2px, #fff 2px, #fff 4px, #000 4px, #000 7px, #fff 7px, #fff 9px, #000 9px, #000 10px, #fff 10px, #fff 13px)',
                  margin: '4px 0',
                }}
              />
              <div style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700 }}>
                003404347843046638{80 + currentPkgNum}
              </div>
            </div>

            {/* Simulated DHL Scanner + Beep Wave */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  background: '#facc15',
                  color: '#0f172a',
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontWeight: 900,
                  fontSize: 11,
                  border: '2px solid #ca8a04',
                }}
              >
                📱 {t('ui.batch.videoQuickStorage')}
              </div>
              <div style={{ marginTop: 6, fontSize: 13, fontWeight: 800, color: isBeepFlash ? '#ffffff' : '#4ade80' }}>
                {isBeepFlash
                  ? `🔊 ${t('ui.batch.videoBeepGreen')}`
                  : `🔦 ${t('ui.batch.videoScanning')}`}
              </div>
            </div>
          </div>
        )}

        {/* Scene 2: Pause on X (resumes at exact position) + Zoomable original photo */}
        {scene === 2 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 10, flexWrap: 'wrap', margin: 'auto 0' }}>
            <div style={{ background: '#1e293b', border: '1px solid #f59e0b', borderRadius: 10, padding: '9px 12px', flex: '1 1 140px' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#fbbf24' }}>
                ⏸️ {t('ui.batch.videoPauseCustomer')}
              </div>
              <div style={{ fontSize: 11, color: '#e2e8f0', marginTop: 4 }}>
                {t('ui.batch.videoProgress')} <strong>#8 / 15</strong> {t('ui.batch.videoStaysSaved')}
              </div>
              <div style={{ fontSize: 10, color: '#4ade80', marginTop: 3 }}>
                ✓ {t('ui.batch.videoMarkedScanned')}
              </div>
            </div>
            <div style={{ background: '#1e293b', border: '1px solid #38bdf8', borderRadius: 10, padding: '9px 12px', flex: '1 1 140px' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#38bdf8' }}>
                🖼️ {t('ui.batch.videoOriginalPhoto')}
              </div>
              <div style={{ fontSize: 11, color: '#e2e8f0', marginTop: 4 }}>
                {t('ui.batch.videoCodeFails')}
              </div>
            </div>
          </div>
        )}

        {/* Scene 3: 7-Day Batch History & Chronik Filter by Date & Time */}
        {scene === 3 && (
          <div style={{ background: '#1e293b', border: '1px solid #60a5fa', borderRadius: 10, padding: '10px 12px', margin: 'auto 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#93c5fd' }}>
                🕒 {t('ui.batch.videoRecentHistory')}
              </span>
              <span style={{ fontSize: 10, background: '#0f172a', padding: '2px 7px', borderRadius: 5, color: '#cbd5e1' }}>
                📅 {t('ui.batch.videoFilterDemo')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f172a', padding: '6px 10px', borderRadius: 7, fontSize: 11 }}>
              <span>
                📦 {t('ui.batch.videoBatchDemo')}
              </span>
              <span style={{ background: '#2563eb', color: '#fff', padding: '3px 8px', borderRadius: 5, fontWeight: 700, fontSize: 10 }}>
                ↩️ {t('ui.batch.videoReopen')}
              </span>
            </div>
          </div>
        )}

        {/* Current Scene Subtitle */}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                textTransform: 'uppercase',
                background: '#2563eb',
                color: '#fff',
                padding: '2px 7px',
                borderRadius: 4,
              }}
            >
              {scenes[scene].badge}
            </span>
            <strong style={{ fontSize: 13, color: '#fff' }}>{scenes[scene].title}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.45 }}>{scenes[scene].desc}</div>
        </div>
      </div>

      {/* Scene Selector Tabs + Progress Bars */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, padding: '8px 10px', background: '#0f172a' }}>
        {scenes.map((s, idx) => {
          const active = idx === scene;
          const pct = idx < scene ? 100 : idx === scene ? tick : 0;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => selectScene(idx)}
              style={{
                background: active ? '#1e293b' : 'transparent',
                border: `1px solid ${active ? '#38bdf8' : '#334155'}`,
                borderRadius: 6,
                padding: '5px 6px',
                cursor: 'pointer',
                textAlign: 'left',
                color: active ? '#fff' : '#94a3b8',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {idx + 1}.{' '}
                {idx === 0
                  ? t('ui.batch.videoTab1')
                  : idx === 1
                  ? `🎙️ ${t('ui.batch.videoTab2')}`
                  : idx === 2
                  ? `⏸️ ${t('ui.batch.videoTab3')}`
                  : `🕒 ${t('ui.batch.videoTab4')}`}
              </div>
              <div style={{ height: 3, background: '#334155', borderRadius: 2, marginTop: 4, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: '#38bdf8', transition: 'width 0.1s linear' }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

